import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Power,
  ChevronLeft,
  Menu,
  Banknote,
  LucideShoppingCart,
  CalendarCheck,
  UserCircle,
  BarChart3,
} from "lucide-react";

import { useGlobalContext } from "../../../GlobalContext";
import ConfirmLogoutModal from "../../Utils/ConfirmLogoutModal";
import Logo from "../../../Images/Packky Logo Blue.svg";
import Logo1 from "../../../Images/Packky small blue.svg";

const iconMap = {
  Volunteer: UserCircle,
  // "Volunteer Team": Users,
  "Volunteer Shops": LucideShoppingCart,
  Attendance: CalendarCheck,
  Bank: Banknote,
  Settlement: BarChart3,
};


export default function VolunteerSidebar({ sidebarOpen, setSidebarOpen }) {

  const navigate = useNavigate();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState([]);
  const [volunteerInfo, setVolunteerInfo] = useState(null);
  const [isOnline, setIsOnline] = useState(true);

  const { logoutVolunteer } = useGlobalContext();
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("volunteerData");
      if (raw) {
        const parsed = JSON.parse(raw);
        setVolunteerInfo(parsed);
      }
    } catch (error) {
      console.error("Failed to parse volunteerData from session", error);
    }
  }, []);
  const handleVolunteerLogout = () => {
    logoutVolunteer();
    navigate("/volunteer/login", { replace: true });
  };

const VolunteerStatusCard = ({ collapsed = false }) => {
  if (!volunteerInfo) return null;

  const name = volunteerInfo?.userName || "Volunteer";
  const role =
    volunteerInfo?.role || volunteerInfo?.userType || "User";

  if (collapsed) {
    return (
      <div className="px-2 pb-3">
        <div className="rounded-2xl bg-white/14 border border-white/20 p-2 flex flex-col items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-white text-[#FC981B] flex items-center justify-center font-bold text-sm shadow-sm">
            {name?.charAt(0)?.toUpperCase() || "V"}
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <div className="rounded-2xl border border-white/20 bg-white/14 backdrop-blur-md p-3 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-white text-[#FC981B] flex items-center justify-center font-bold text-base shadow-sm shrink-0">
            {name?.charAt(0)?.toUpperCase() || "V"}
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white truncate">
              {name}
            </div>
            <div className="text-[11px] text-white/75 truncate mt-0.5">
              {role}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <span
              className={`h-2.5 w-2.5 rounded-full ${
                isOnline ? "bg-emerald-400" : "bg-slate-300"
              }`}
            />
            <span className="text-xs font-medium text-white">
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>

        </div>
      </div>
    </div>
  );
};

  useEffect(() => {
    const customMenu = [
      { menu_title: "Volunteer", menu_url: "/volunteer" },
      // { menu_title: "Volunteer Team", menu_url: "/volunteer/team" },
      { menu_title: "Volunteer Shops", menu_url: "/volunteer/shops" },
      { menu_title: "Attendance", menu_url: "/volunteer/attendance" },
      { menu_title: "Bank", menu_url: "/volunteer/bank" },
      { menu_title: "Settlement", menu_url: "/volunteer/settlement" },
    ];

    setMenuItems(customMenu);
  }, []);

  return (
    <>
      {/* Mobile Toggle Button - Only visible on mobile */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 right-4 z-50 p-2 rounded-lg shadow-lg"
        style={{
          backgroundColor: "#2563EB",
          color: "#ffff",
        }}
      >
        <Menu size={13} />
      </button>

      {/* Mobile Top Navigation - Only visible on mobile when sidebar is open */}
      {sidebarOpen && (
        <>
          {/* Mobile Overlay */}
          <div
            className="md:hidden fixed inset-0   z-30"
            onClick={() => setSidebarOpen(false)}
          />

          {/* Mobile Top Nav */}
          <div
            className="md:hidden fixed top-0 left-0 right-0 z-40 p-2 pt-2"
            style={{
              backgroundColor: "#2563EB",
              color: "#ffff",
              fontWeight: "600",
            }}
          >
            {/* Header with logo and close button */}
            <div className="flex items-center justify-between mb-4">
              <div
                className="h-10 content-center"
                style={{
                  padding: "10px",
                  backgroundColor: "white",
                  borderRadius: "1.5rem",
                }}
              >
                <img className="h-6" src={Logo} alt="" />
              </div>

              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
              >
                {/* <X size={24} /> */}
              </button>
            </div>

            {/* Navigation Menu - Horizontal scroll on mobile */}
            <div
              className="block md:hidden flex overflow-x-auto space-x-2 px-2 py-1 scrollbar-hide bg-[#2563EB] text-white text-sm"
              style={{ height: "52px" }}
            >
              {menuItems.map((item) => {
                const routePath = item.menu_url;
                const isActive = location.pathname === routePath;
                const Icon = iconMap[item.menu_title] || LayoutDashboard;

                return (
                  <div
                    key={item.menu_title}
                    onClick={() => navigate(routePath)}
                    title={!sidebarOpen ? item.menu_title : ""}
                    className={`flex items-center gap-3 p-3 cursor-pointer transition-all rounded-md ${isActive
                        ? "bg-white/20 border-l-4 border-white"
                        : "hover:bg-white/20"
                      }`}
                  >
                    <Icon size={20} />
                    {sidebarOpen && (
                      <span className="text-sm">{item.menu_title}</span>
                    )}
                  </div>
                );
              })}

              <ConfirmLogoutModal
                onConfirm={handleVolunteerLogout}
                trigger={
                  <div
                    className="flex flex-col items-center justify-center px-2 cursor-pointer hover:bg-red-500 transition-colors rounded-lg"
                    style={{ minWidth: "54px" }}
                  >
                    <Power size={14} />
                    <span className="text-[10px]">Logout</span>
                  </div>
                }
              />
            </div>
          </div>
        </>
      )}

      {/* Desktop/Tablet Sidebar - Hidden on mobile, visible on md and up */}
      <div
        className={`hidden md:flex ${sidebarOpen ? "w-56" : "md:w-16"
          } transition-all duration-200 ease-in-out flex-col h-full`}
        style={{
          backgroundColor: "#2563EB",
          color: "#ffff",
          fontWeight: "600",
        }}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <span className="text-lg font-bold whitespace-nowrap">
            {sidebarOpen ? (
              <div
                className="h-15 content-center"
                style={{
                  padding: "15px",
                  backgroundColor: "white",
                  borderRadius: "2rem",
                }}
              >
                <img className="h-7" src={Logo} alt="" />
              </div>
            ) : (
              ""
            )}
          </span>

          {/* Desktop toggle button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            {sidebarOpen ? (
              <ChevronLeft size={20} />
            ) : (
              <div className=" bg-white rounded">
                <img className=" bg-white rounded " src={Logo1} alt="" />
              </div>
            )}
          </button>
        </div>

        <div className="flex-1 py-2 space-y-2">
          {menuItems.map((item) => {
            const routePath = item.menu_url;
            const isActive = location.pathname === routePath;
            const Icon = iconMap[item.menu_title] || LayoutDashboard;

            return (
              <div
                key={item.menu_title}
                onClick={() => navigate(routePath)}
                title={!sidebarOpen ? item.menu_title : ""}
                className={`flex items-center gap-3 p-3 cursor-pointer transition-all ${isActive
                    ? "bg-white/20 border-l-4 border-white"
                    : "hover:bg-white/20"
                  }`}
              >
                <Icon size={20} />
                {sidebarOpen && (
                  <span className="text-sm">{item.menu_title}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-white/20 pt-3">
          <VolunteerStatusCard collapsed={!sidebarOpen} />

          <div className="px-3 pb-4">
            <ConfirmLogoutModal
              onConfirm={handleVolunteerLogout}
              trigger={
                <div className="flex items-center justify-center gap-2 cursor-pointer rounded-xl border border-white/15 bg-white/10 hover:bg-red-500 transition-all px-3 py-2 text-white">
                  <Power size={18} />
                  {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
                </div>
              }
            />
          </div>
        </div>
      </div>
    </>
  );
}
